import { Button, Input, Select, Space, Switch, TableProps } from "antd";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import TitleBar from "../../../../components/common/TitleBar";
import CustomTable from "../../../../components/ui/CustomTable";
import {
  TGetAllSurveyTemplate,
  TGetSurveyTemplateParams,
} from "../../../../features/organization/survey/survey.type";
import {
  useGetAllSurveyTemplateQuery,
  useSurveyTemplateUpdateBulkStatusMutation,
} from "../../../../features/organization/survey/surveyApi";
import useError from "../../../../hooks/useError";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../utils/string";
import { addKeyInArray, selectSearchOption } from "../../../../utils/utils";

export default function TemplatesList() {
  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handlePagination,
    handleSearch,
  } = useTable<TGetSurveyTemplateParams>();
  const { user } = useAppSelector((state) => state.auth);
  const [
    surveyTemplateUpdateBulkStatus,
    {
      isLoading: isCreateLoading,
      isError: isCreateError,
      error: createError,
      isSuccess: isCreateSuccess,
    },
  ] = useSurveyTemplateUpdateBulkStatusMutation();
  const { isError, error, isLoading, isFetching, data } =
    useGetAllSurveyTemplateQuery({
      params: {
        ...filter,
        search: keyword,
        business_id: user?.business_id,
      },
    });
  useError(isError, error, "Survey template get fail");
  useError(isCreateError, createError, "Status update fail");

  useEffect(() => {
    if (isCreateSuccess) {
      toast("success", "Status updated successfully");
    }
  }, [isCreateSuccess]);

  const columns: TableProps<TGetAllSurveyTemplate["templates"][0]>["columns"] =
    [
      {
        title: "#SL",
        align: "center",
        width: 60,
        render: (_, __, index) => countParPageSerialNumber(page, limit, index),
      },
      {
        title: "Template Name",
        dataIndex: "title",
        render: (val) => valueOrNa(val),
      },
      {
        title: "Created at",
        dataIndex: "created_at",
        render: (val) => (val ? formatDate(val) : valueOrNa(val)),
      },
      {
        title: "Total Question",
        dataIndex: "surveyQuestion",
        render: (val) => val?.length || 0,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (val, record) => (
          <Space size={4}>
            <Switch
              className="custom-switch"
              defaultChecked
              checked={val === "active"}
              onChange={() =>
                surveyTemplateUpdateBulkStatus({
                  ids: [record.id],
                  status: val === "active" ? "inactive" : "active",
                })
              }
              disabled={isCreateLoading || isFetching}
            />
            <span className="text-header text-sm font-medium">
              {capitalize(val)}
            </span>
          </Space>
        ),
      },
      {
        title: "Action",
        dataIndex: "id",
        align: "center",
        width: 130,
        render: (val) => (
          <Space align="center">
            <Link to={`details/${val}`}>
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
              />
            </Link>

            <Link to={`update/${val}`}>
              <Button
                className="action-edit"
                size="small"
                icon={<EditIcon />}
              />
            </Link>
          </Space>
        ),
      },
    ];

  return (
    <SidebarLayout title="Survey">
      <TitleBar title="Templates">
        <Link to="add">
          <Button icon={<AddIcon />} type="primary" className="sm-button !px-3">
            Create a Template
          </Button>
        </Link>
      </TitleBar>

      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total}
        dataSource={addKeyInArray(data?.data?.templates)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={statusList}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter.status}
              onChange={(val) => handleFilter("status", val)}
              popupMatchSelectWidth={120}
            />
          </>
        }
      />
    </SidebarLayout>
  );
}

const statusList = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "inactive",
  },
];
